<template>
  <div class="caregiver-app min-h-screen w-full bg-slate-50 flex flex-col text-base">
    <!-- 任务详情全屏 -->
    <caregiver-task-detail
      v-if="activeView === 'taskDetail' && currentTask"
      :task="currentTask"
      @back="closeTaskDetail"
      @update="onTaskUpdate"
    />

    <template v-else>
      <!-- Tab 1: 工作台 -->
      <div v-show="activeTab === 'workbench'" class="flex-1 flex flex-col">
        <header
          class="bg-family-blue text-white rounded-b-[1.75rem] px-4 pb-8"
          :style="{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }"
        >
          <div class="flex items-start justify-between mb-5">
            <div>
              <p class="text-sm text-white/75">欢迎回来</p>
              <div class="flex items-center gap-2 mt-1">
                <h1 class="text-2xl font-bold tracking-tight">{{ profile.name }}</h1>
                <span class="px-2 py-0.5 rounded-md text-xs font-bold bg-white/20 border border-white/20">{{ profile.level }}</span>
              </div>
            </div>
            <div class="w-12 h-12 rounded-full bg-white/25 border border-white/30 flex items-center justify-center text-lg font-bold">
              {{ avatarLetter }}
            </div>
          </div>

          <!-- 接单开关 -->
          <div class="bg-[#006bb3]/60 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-base font-bold">接单状态</p>
              <p class="text-sm text-white/75 mt-0.5 truncate">
                {{ acceptingOrders ? "正在接单中，可接收新任务" : "已暂停接单" }}
              </p>
            </div>
            <button
              type="button"
              class="relative w-[52px] h-7 rounded-full transition-colors duration-300 flex-shrink-0"
              :class="acceptingOrders ? 'bg-white' : 'bg-white/30'"
              @click="acceptingOrders = !acceptingOrders"
            >
              <span
                class="absolute top-0.5 w-6 h-6 rounded-full shadow-md transition-all duration-300"
                :class="acceptingOrders ? 'left-[26px] bg-family-blue' : 'left-0.5 bg-white'"
              />
            </button>
          </div>
        </header>

        <main class="flex-1 px-4 -mt-6 pb-4 overflow-y-auto">
          <!-- 统计 -->
          <div class="bg-white rounded-2xl p-5 mb-5 shadow-[0_4px_20px_rgb(0,0,0,0.06)] grid grid-cols-2 gap-4">
            <div class="text-center">
              <p class="text-3xl font-bold text-slate-900">{{ profile.totalHours }}</p>
              <p class="text-sm text-slate-400 mt-1.5">累计服务时长(小时)</p>
            </div>
            <div class="text-center border-l border-slate-100">
              <p class="text-3xl font-bold text-slate-900">{{ profile.monthIncome }}</p>
              <p class="text-sm text-slate-400 mt-1.5">本月应收工资(元)</p>
            </div>
          </div>

          <!-- 当前任务 -->
          <h2 class="text-lg font-bold text-slate-800 mb-3 px-0.5">当前任务</h2>

          <article
            v-if="activeServingTask"
            class="bg-white rounded-2xl p-4 mb-5 shadow-sm border border-blue-100 cursor-pointer active:scale-[0.99] transition-transform"
            @click="openTask(activeServingTask)"
          >
            <div class="flex items-center justify-between mb-3">
              <span class="text-sm text-slate-400">{{ activeServingTask.orderNo }}</span>
              <span class="px-2.5 py-0.5 rounded-md text-sm font-bold bg-emerald-50 text-emerald-600">服务中</span>
            </div>
            <p class="text-xl font-bold text-slate-900 leading-snug">{{ activeServingTask.hospital }}</p>
            <p class="text-base text-slate-500 mt-1.5">{{ activeServingTask.department }} · {{ activeServingTask.bedNo }}</p>
            <div class="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3 text-sm text-slate-500">
              <span class="flex items-center gap-1.5">
                <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                {{ activeServingTask.patientName }} {{ activeServingTask.patientAge }}岁
              </span>
              <span class="flex items-center gap-1.5">
                <svg class="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                {{ activeServingTask.startDate }} ~ {{ activeServingTask.endDate }}
              </span>
            </div>
          </article>

          <div v-else class="bg-white rounded-2xl p-8 mb-5 text-center text-slate-400 border border-slate-100">
            <p class="text-lg">暂无进行中的任务</p>
            <p class="text-sm mt-1">开启接单后等待调度员派单</p>
          </div>

          <!-- 快捷操作 -->
          <h2 class="text-lg font-bold text-slate-800 mb-3 px-0.5">快捷操作</h2>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left flex items-center gap-3 active:scale-[0.98] transition-transform"
              @click="activeTab = 'tasks'"
            >
              <div class="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center text-family-blue flex-shrink-0">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
              </div>
              <div>
                <p class="text-base font-bold text-slate-800">任务列表</p>
                <p class="text-sm text-slate-400 mt-0.5">查看全部</p>
              </div>
            </button>
            <button
              type="button"
              class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 text-left flex items-center gap-3 active:scale-[0.98] transition-transform"
              @click="activeTab = 'profile'"
            >
              <div class="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center text-caregiver-green flex-shrink-0">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
              </div>
              <div>
                <p class="text-base font-bold text-slate-800">提现申请</p>
                <p class="text-sm text-slate-400 mt-0.5">工资结算</p>
              </div>
            </button>
          </div>
        </main>
      </div>

      <!-- Tab 2: 任务列表 -->
      <div v-show="activeTab === 'tasks'" class="flex-1 flex flex-col">
        <header
          class="bg-family-blue text-white px-4 pb-5"
          :style="{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }"
        >
          <h1 class="text-2xl font-bold">任务列表</h1>
          <p class="text-sm text-white/80 mt-1">共 {{ tasks.length }} 个任务</p>
          <div class="flex gap-2 mt-4">
            <button
              v-for="tab in taskFilters"
              :key="tab.value"
              type="button"
              class="flex-1 py-2 rounded-xl text-base font-bold transition-all"
              :class="taskFilter === tab.value ? 'bg-white text-family-blue' : 'bg-white/20 text-white'"
              @click="taskFilter = tab.value"
            >
              {{ tab.label }}
            </button>
          </div>
        </header>

        <main class="flex-1 px-4 py-4 overflow-y-auto">
          <article
            v-for="task in filteredTasks"
            :key="task.id"
            class="bg-white rounded-2xl p-4 mb-3 shadow-sm border border-slate-100 cursor-pointer active:scale-[0.99]"
            @click="openTask(task)"
          >
            <div class="flex items-center justify-between mb-2">
              <span class="text-sm text-slate-400">{{ task.orderNo }}</span>
              <span class="px-2 py-0.5 rounded text-sm font-bold" :class="statusTag(task.status).cls">
                {{ statusTag(task.status).label }}
              </span>
            </div>
            <p class="text-xl font-bold text-slate-900">{{ task.hospital }}</p>
            <p class="text-base text-slate-500 mt-1">{{ task.department }} · {{ task.bedNo }}</p>
            <div class="flex items-center gap-4 mt-3 text-sm text-slate-500">
              <span>{{ task.patientName }}</span>
              <span>{{ task.startDate }}</span>
            </div>
            <div
              v-if="task.status === 'serving'"
              class="mt-3 py-2.5 px-3 rounded-xl bg-blue-50 text-family-blue text-base font-bold flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
              已记录 {{ logCount(task) }} 条护理日志
            </div>
            <div
              v-if="task.status === 'pending_confirm_task'"
              class="mt-3 py-2.5 px-3 rounded-xl bg-red-50 text-red-500 text-base font-bold flex items-center gap-2"
            >
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              请在 10 分钟内确认
            </div>
          </article>
        </main>
      </div>

      <!-- Tab 3: 我的 -->
      <div v-show="activeTab === 'profile'" class="flex-1 flex flex-col overflow-y-auto">
        <header
          class="bg-family-blue text-white px-4 pb-8"
          :style="{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }"
        >
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-full bg-white/20 border border-white/25 flex items-center justify-center text-2xl font-bold">
              {{ avatarLetter }}
            </div>
            <div>
              <p class="text-2xl font-bold">{{ profile.name }}</p>
              <div class="flex gap-2 mt-2">
                <span class="px-2 py-0.5 rounded text-xs font-bold bg-white/20">{{ profile.level }}</span>
                <span class="px-2 py-0.5 rounded text-xs font-bold bg-white/20">{{ profile.experience }}</span>
              </div>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3 mt-5">
            <div class="bg-white/15 rounded-xl p-3 text-center">
              <p class="text-xs text-white/70">累计服务</p>
              <p class="text-xl font-bold mt-1">{{ profile.totalHours }} 小时</p>
            </div>
            <div class="bg-white/15 rounded-xl p-3 text-center">
              <p class="text-xs text-white/70">本月应收</p>
              <p class="text-xl font-bold mt-1">¥{{ profile.monthIncome }}</p>
            </div>
          </div>
        </header>

        <main class="px-4 -mt-5 pb-24">
          <div class="bg-white rounded-2xl p-4 mb-3 shadow-[0_4px_20px_rgb(0,0,0,0.06)]">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                </div>
                <div>
                  <p class="text-lg font-bold text-slate-800">工资提现</p>
                  <p class="text-sm text-slate-400">人工审核，财务线下打款</p>
                </div>
              </div>
              <span class="text-2xl font-bold text-red-500">¥{{ profile.monthIncome }}</span>
            </div>
            <button
              type="button"
              class="w-full min-h-[52px] rounded-xl bg-red-500 text-white text-lg font-bold active:scale-[0.98] transition-transform"
              @click="$message.info('提现申请已提交，等待财务审核')"
            >
              申请提现
            </button>
          </div>

          <div class="space-y-3">
            <button
              v-for="item in profileMenus"
              :key="item.label"
              type="button"
              class="w-full flex items-center gap-3 px-4 py-4 text-left bg-white rounded-2xl shadow-sm border border-slate-100 active:scale-[0.99] transition-transform"
            >
              <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="item.bg">
                <span v-html="item.icon" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-lg font-bold text-slate-800">{{ item.label }}</p>
                <p class="text-sm text-slate-400 truncate">{{ item.desc }}</p>
              </div>
              <svg class="w-5 h-5 text-slate-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>

          <p class="text-center text-sm text-slate-300 mt-6">安心陪护护工端 v0.1.0</p>
        </main>
      </div>

      <!-- 底部导航 -->
      <nav
        class="sticky bottom-0 z-40 bg-white border-t border-slate-100"
        :style="{ paddingBottom: 'env(safe-area-inset-bottom)' }"
      >
        <div class="flex h-14">
          <button
            v-for="tab in bottomTabs"
            :key="tab.key"
            type="button"
            class="flex-1 flex flex-col items-center justify-center gap-0.5"
            :class="activeTab === tab.key ? 'text-family-blue' : 'text-slate-400'"
            @click="activeTab = tab.key"
          >
            <span v-html="tab.icon" class="w-6 h-6" />
            <span class="text-xs font-bold">{{ tab.label }}</span>
          </button>
        </div>
      </nav>
    </template>

    <!-- 新任务指派弹窗 -->
    <transition name="modal-fade">
      <div
        v-if="showNewTaskModal && pendingTask"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-4 pb-4"
        @click.self="showNewTaskModal = false"
      >
        <div class="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl">
          <div class="bg-gradient-to-br from-red-500 to-red-600 px-6 py-8 text-center text-white">
            <div class="w-16 h-16 mx-auto rounded-full bg-white/20 flex items-center justify-center mb-4">
              <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <h3 class="text-2xl font-bold">新任务指派</h3>
            <p class="text-sm text-white/80 mt-2">调度员已为您匹配订单</p>
          </div>
          <div class="p-5">
            <div class="bg-red-50 rounded-2xl p-4 mb-4">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-red-500 text-xl">🏥</span>
                <p class="text-xl font-bold text-slate-900">{{ pendingTask.hospital }}</p>
              </div>
              <p class="text-base text-slate-600">{{ pendingTask.department }} · {{ pendingTask.bedNo }}</p>
              <p class="text-base text-slate-700 mt-3 font-medium">
                {{ pendingTask.patientName }} · {{ pendingTask.patientAge }}岁 · {{ pendingTask.patientGender }} · {{ pendingTask.careLevel }}
              </p>
              <p class="text-sm text-slate-400 mt-2">{{ pendingTask.startDate }} ~ {{ pendingTask.endDate }}</p>
            </div>
            <p class="text-center text-red-500 text-base font-bold mb-5 flex items-center justify-center gap-2">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
              剩余确认时间：{{ countdownText }}
            </p>
            <div class="grid grid-cols-2 gap-3">
              <button
                type="button"
                class="min-h-[52px] rounded-xl bg-slate-100 text-slate-600 text-lg font-bold"
                @click="rejectTask"
              >
                拒绝
              </button>
              <button
                type="button"
                class="min-h-[52px] rounded-xl bg-family-blue text-white text-lg font-bold"
                @click="acceptTask"
              >
                确认接单
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import CaregiverTaskDetail from "./CaregiverTaskDetail.vue";
import { TASK_PHASE } from "./caregiverTaskMachine";

const TAB_ICONS = {
  workbench: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>',
  tasks: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>',
  profile: '<svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>',
};

function makeTask(overrides) {
  return {
    id: Date.now(),
    orderNo: "AX26062400123",
    hospital: "上海市第一人民医院",
    department: "住院部 3 楼 神经外科",
    bedNo: "16 床",
    wardLocation: "住院部 3 楼 神经外科 · 16 床",
    patientName: "李建国",
    patientAge: 72,
    patientGender: "男",
    careLevel: "半自理",
    familyPhone: "13698765432",
    familyPhoneMasked: "136****5432",
    startDate: "2026-06-24",
    endDate: "2026-07-08",
    status: "serving",
    phase: TASK_PHASE.DEPARTING,
    inGeofence: false,
    logs: {
      "2026-06-24": { fed: true, turned: true, mood: "good", submittedAt: Date.now() - 86400000 },
      "2026-06-25": { fed: true, turned: true, mood: "normal", submittedAt: Date.now() },
    },
    machineState: null,
    ...overrides,
  };
}

export default {
  name: "CaregiverApp",
  components: { CaregiverTaskDetail },
  data() {
    return {
      activeTab: "workbench",
      activeView: "main",
      currentTask: null,
      acceptingOrders: true,
      taskFilter: "all",
      showNewTaskModal: false,
      countdown: 600,
      countdownTimer: null,
      profile: {
        name: "王秀英",
        level: "金牌",
        experience: "8 年经验",
        totalHours: 1864,
        monthIncome: 4280,
      },
      taskFilters: [
        { label: "全部", value: "all" },
        { label: "进行中", value: "active" },
        { label: "已完工", value: "done" },
      ],
      tasks: [
        makeTask({
          id: 1,
          orderNo: "AX26062400123",
          phase: TASK_PHASE.SERVING,
          status: "serving",
        }),
        makeTask({
          id: 2,
          orderNo: "AX26062500309",
          hospital: "四川大学华西医院",
          department: "住院部 3 楼 神经外科 ICU",
          bedNo: "12 床",
          wardLocation: "住院部 3 楼 神经外科 ICU · 12 床",
          patientName: "张德明",
          patientAge: 68,
          patientGender: "男",
          careLevel: "全瘫",
          familyPhone: "13812345678",
          familyPhoneMasked: "138****5678",
          startDate: "2026-06-25",
          endDate: "2026-07-05",
          status: "pending_confirm_task",
          phase: TASK_PHASE.DEPARTING,
          logs: {},
        }),
      ],
      pendingTask: null,
      profileMenus: [
        { label: "我的资质", desc: "身份证、健康证、职业资格证", bg: "bg-blue-50", icon: '<svg class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0v1"/></svg>' },
        { label: "服务记录", desc: "历史任务与收入明细", bg: "bg-emerald-50", icon: '<svg class="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' },
        { label: "帮助中心", desc: "常见问题与客服联系", bg: "bg-red-50", icon: '<svg class="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' },
        { label: "设置", desc: "账号安全、通知设置", bg: "bg-slate-100", icon: '<svg class="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>' },
      ],
      bottomTabs: [
        { key: "workbench", label: "工作台", icon: TAB_ICONS.workbench },
        { key: "tasks", label: "任务", icon: TAB_ICONS.tasks },
        { key: "profile", label: "我的", icon: TAB_ICONS.profile },
      ],
    };
  },
  computed: {
    avatarLetter() {
      return (this.profile.name || "护").charAt(0);
    },
    activeServingTask() {
      return this.tasks.find((t) => t.status === "serving");
    },
    filteredTasks() {
      if (this.taskFilter === "all") return this.tasks;
      if (this.taskFilter === "active") {
        return this.tasks.filter((t) => t.status === "serving" || t.status === "pending_confirm_task");
      }
      return this.tasks.filter((t) => t.status === "done");
    },
    countdownText() {
      const m = Math.floor(this.countdown / 60);
      const s = this.countdown % 60;
      return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    },
  },
  mounted() {
    document.title = "安心陪护 - 护工端";
    document.documentElement.classList.add("caregiver-route");
    document.body.classList.add("caregiver-route");
    this.initPendingTaskModal();
  },
  beforeDestroy() {
    document.documentElement.classList.remove("caregiver-route");
    document.body.classList.remove("caregiver-route");
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  },
  methods: {
    initPendingTaskModal() {
      const pending = this.tasks.find((t) => t.status === "pending_confirm_task");
      if (pending && this.acceptingOrders) {
        setTimeout(() => {
          this.pendingTask = { ...pending };
          this.showNewTaskModal = true;
          this.countdown = 600;
          this.countdownTimer = setInterval(() => {
            if (this.countdown > 0) this.countdown -= 1;
          }, 1000);
        }, 1500);
      }
    },
    statusTag(status) {
      const map = {
        serving: { label: "服务中", cls: "bg-emerald-50 text-emerald-600" },
        pending_confirm_task: { label: "新任务待确认", cls: "bg-red-50 text-red-500" },
        pending_confirm: { label: "待确认完工", cls: "bg-orange-50 text-orange-500" },
        done: { label: "已完工", cls: "bg-slate-100 text-slate-500" },
      };
      return map[status] || map.serving;
    },
    logCount(task) {
      if (!task.logs) return 0;
      return Object.keys(task.logs).filter((k) => task.logs[k] && task.logs[k].fed !== null).length;
    },
    openTask(task) {
      const ms = task.machineState || {};
      const merged = {
        ...task,
        ...ms,
        phase: ms.phase || task.phase,
        logs: ms.logs || task.logs,
      };
      this.currentTask = merged;
      this.activeView = "taskDetail";
    },
    closeTaskDetail() {
      this.activeView = "main";
      this.currentTask = null;
    },
    onTaskUpdate(payload) {
      const idx = this.tasks.findIndex((t) => t.id === payload.id);
      if (idx === -1) return;
      const task = this.tasks[idx];
      task.machineState = payload.machineState;
      task.phase = payload.machineState.phase;
      task.logs = payload.machineState.logs;
      task.status = payload.status;
      if (payload.status === "pending_confirm") {
        task.status = "pending_confirm";
      }
      this.$set(this.tasks, idx, { ...task });
      if (this.currentTask && this.currentTask.id === payload.id) {
        this.currentTask = { ...this.currentTask, ...task, ...payload.machineState };
      }
    },
    acceptTask() {
      const idx = this.tasks.findIndex((t) => t.id === this.pendingTask.id);
      if (idx !== -1) {
        this.tasks[idx].status = "serving";
        this.tasks[idx].phase = TASK_PHASE.DEPARTING;
      }
      this.showNewTaskModal = false;
      this.$message.success("接单成功！");
      if (this.countdownTimer) clearInterval(this.countdownTimer);
    },
    rejectTask() {
      const idx = this.tasks.findIndex((t) => t.id === this.pendingTask.id);
      if (idx !== -1) this.tasks.splice(idx, 1);
      this.showNewTaskModal = false;
      this.$message.info("已拒绝该任务");
      if (this.countdownTimer) clearInterval(this.countdownTimer);
    },
  },
};
</script>

<style scoped>
.caregiver-app {
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Hiragino Sans GB",
    "Microsoft YaHei", sans-serif;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>

<style>
html.caregiver-route,
body.caregiver-route {
  min-height: 100%;
  height: auto;
  width: 100%;
  background: #f8fafc;
  font-size: 16px;
}

body.caregiver-route #app {
  min-height: 100%;
  height: auto;
  width: 100%;
  background: #f8fafc;
}
</style>
